import { useState, useCallback, useEffect } from 'react';
import { useFirestore } from 'reactfire';
import { getCDNWidgetWorkbookUrl } from '@/services/cdn';
import { createWrappedError } from '@/utils/errors';
import workbooks from '@/services/firestore/workbooks';
import workbookStorage from '@/services/storage/workbooks';
const PREFIX = 'common: hooks: useDownloadWidgetsWorkbook:';

// Download the widget's workbook and its data
const useDownloadWidgetsWorkbook = function () {
  let {
    widget,
    onError,
    onSuccess
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const firestore = useFirestore();
  const {
    0: error,
    1: setError
  } = useState(null);
  const {
    0: status,
    1: setStatus
  } = useState('pending');
  const {
    0: data,
    1: setData
  } = useState(null);
  const handleError = useCallback(err => {
    setError(err);
    setStatus('failure');
    setData(null);
    typeof onError === 'function' && onError(err); // eslint-disable-next-line
  }, []);
  const handleSuccess = useCallback((data, widget) => {
    setData(data);
    setStatus('success');
    setError(null);
    typeof onSuccess === 'function' && onSuccess(data, widget); // eslint-disable-next-line
  }, []);
  const refetch = useCallback(async () => {
    var _widget$workbook;

    // If widget is not provided, do nothing
    if (!widget) return;

    if (!((_widget$workbook = widget.workbook) !== null && _widget$workbook !== void 0 && _widget$workbook.id)) {
      handleError(new Error(`${PREFIX} widget or workbook ID is missing`));
      return;
    }

    const widgetWorkbookUrl = widget !== null && widget !== void 0 && widget.id ? getCDNWidgetWorkbookUrl(widget.id) : '';

    if (!widgetWorkbookUrl) {
      handleError(new Error(`${PREFIX} widget workbook URL is missing`));
      return;
    }

    setStatus('loading');
    let workbookResult = null;

    try {
      var _widget$workbook2;

      workbookResult = await workbooks.findRecordSnapshot(firestore, (_widget$workbook2 = widget.workbook) === null || _widget$workbook2 === void 0 ? void 0 : _widget$workbook2.id);
    } catch (err) {
      var _widget$workbook3;

      const wrappedError = createWrappedError(`${PREFIX} failed to find workbook record ID: ${(_widget$workbook3 = widget.workbook) === null || _widget$workbook3 === void 0 ? void 0 : _widget$workbook3.id}`, err);
      handleError(wrappedError);
      return;
    }

    let workbookData = null;

    try {
      const result = await workbookStorage.downloadWorkbook(widgetWorkbookUrl);
      workbookData = result.data;
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} failed to download workbook from "${widgetWorkbookUrl}"`, err);
      handleError(wrappedError);
      return;
    }

    if (workbookResult && workbookData) {
      handleSuccess({
        workbook: workbookResult,
        workbookData: workbookData
      }, widget);
    }
  }, [widget, firestore, handleError, handleSuccess]);
  useEffect(() => {
    if (!widget) return;
    refetch();
  }, [widget, refetch]);
  return {
    status,
    error,
    data,
    refetch
  };
};

export default useDownloadWidgetsWorkbook;